import React from "react";
import { authFetch } from "../../HttpClient";
import { useEtatGlobal } from "../../pages/EtatGlobal";
import { useEvaContexte } from "./evaContexte";
import RdvHud from "../RdvHud/RdvHud";
import "./EvaChat.css";

// EVA, l'assistante IA de MyALFA, disponible sur tous les écrans.
// Design repris du chat des Modules (Procédures / Base de connaissance —
// ProcAssistant, ProcAiMsg, ProcMascot, maquette Claude Design) : mascotte en
// bas à droite, panneau de discussion, suggestions, mise en forme des
// réponses. Côté serveur : /api/eva/chat (droits effectifs de l'utilisateur,
// quota IA du jour), /api/eva/chat/actions/:id (confirmer / annuler une
// action préparée), /api/ia/utilisation (barre « Utilisation »).

const API = "https://backend-myalfa.vercel.app/api";
const HISTORIQUE_MAX = 12;
const CONSERVES_MAX = 30;

// Écrans sans EVA : ils ont déjà leur propre assistant (mêmes mascotte et panneau).
const ECRANS_SANS_EVA = new Set(["Procedures", "KnowledgeBase"]);

const LIBELLES_ECRANS = {
  Dashboard: "Tableau de bord",
  Bureau: "Bureau",
  ToDo: "To-do",
  Annuaire: "Annuaire",
  Mails: "Mails",
  Discussion: "Discussion",
  Planning: "Planning",
  FeuilleTemps: "Feuille de temps",
  Conges: "Congés",
  Admin: "Administration",
  SuperAdmin: "Super Admin",
  AllEvent: "Évènements",
  ticket: "Tickets",
  Mandat: "Mandats",
  Recouvrement: "Recouvrement",
  GedClient: "GED",
  LettresMission: "Lettres de mission",
  AllLead: "Leads",
  LeadMain: "Lead",
  Portefeuille: "Portefeuille",
  Suivi: "Suivi de production",
  Contrat: "Contrats",
  RHelp: "Aide RH",
};

// Profil de l'utilisateur pour adapter les propositions d'EVA — mêmes règles
// que les droits appliqués par le serveur (backend services/evaDroits.js) :
// EVA ne propose jamais une question à laquelle elle refuserait de répondre,
// ni une vue « cabinet » à un collaborateur.
function profilEva(u) {
  const f = Array.isArray(u?.fonctionalite) ? u.fonctionalite : [];
  const admin = f.includes("Admin");
  const a = (module) => admin || f.includes(module);
  const role = u?.role || "";
  const direction = admin || ["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(role);
  return {
    role,
    direction,
    encadrement: direction || role === "SUPERVISEUR",
    secretariat: role === "SECRETARIAT",
    gestionInterne: ["ADMINISTRATEUR", "EXPERT_COMPTABLE", "SECRETARIAT"].includes(role),
    evenementsTous: admin || role === "SECRETARIAT",
    portefeuille: a("Portefeuille"),
    suiviProd: a("Suivi Prod"),
    lead: a("Lead"),
  };
}

// Trois propositions au plus, la plus utile en premier : d'abord le contexte
// de l'écran (fiche client ou prospect ouverte), puis selon le profil.
function suggestionsPour(p, ctx) {
  const s = [];
  if (ctx.clientId && p.suiviProd) s.push("Où en sont les suivis de ce client ?");
  else if (ctx.clientId && p.portefeuille) s.push("Résume-moi la fiche de ce client");
  if (ctx.leadId && p.lead) s.push("Où en est ce prospect ?");

  const rdv = p.evenementsTous ? "Quels rendez-vous sont prévus cette semaine ?" : "Quels sont mes rendez-vous cette semaine ?";
  const taches = "Quelles sont mes tâches en attente ?";
  const lm = "Quelles lettres de mission attendent une signature ?";
  if (p.direction) {
    s.push("Fais-moi un point sur l'activité du cabinet");
    if (p.gestionInterne) s.push(lm);
    s.push(rdv, taches);
  } else if (p.secretariat) {
    s.push(rdv, lm, taches);
  } else if (p.encadrement) {
    s.push("Fais-moi un point sur l'activité de mon équipe", taches, rdv);
  } else {
    // Collaborateur, assistant, pôles : ses propres dossiers uniquement.
    if (p.suiviProd && p.role === "JURIDIQUE") s.push("Où en sont mes dossiers juridiques ?");
    else if (p.suiviProd && p.role !== "SOCIAL") s.push("Où en sont mes bilans cette année ?");
    s.push(taches, rdv);
  }
  s.push("Que peux-tu faire pour moi ?"); // repli, valable pour tous
  return [...new Set(s)].slice(0, 3);
}

// Bulle de la mascotte : une phrase différente à chaque apparition — dès
// l'arrivée sur la plateforme (ou une actualisation), puis environ toutes les
// 30 s, visible 10 s. Pas de bulle tant que le chat est ouvert ; à sa
// fermeture, le compte des 30 s repart.
const PHRASES = [
  "Un dossier à retrouver ? Demandez-moi, je cherche plus vite que mon ombre.",
  "Une TVA à vérifier ? Posez-moi la question, je connais vos suivis sur le bout des circuits.",
  "Vos rendez-vous de la semaine ? Cliquez sur moi, je vous fais le point.",
  "Où en est ce client ? Demandez à EVA, je m'occupe du reste.",
  "Vos tâches en attente en un clin d'œil ? Demandez-moi, c'est ma spécialité.",
  "Un point sur l'activité du cabinet ? Je vous le résume en quelques lignes, il suffit de demander.",
  "Pas le temps de chercher ? Moi, j'ai tout mon temps. Posez-moi votre question.",
  "Une question sur un dossier ? Je réponds sans rendez-vous. Essayez !",
  "Je ne bois pas de café, mais je suis toujours disponible. Une question ?",
  "Besoin d'un coup de main ? Cliquez sur moi, EVA est à votre service.",
  "Plutôt que d'ouvrir dix écrans, posez-moi une question.",
  "Le SIREN d'un client vous échappe ? Demandez-le-moi.",
  "Une échéance à ne pas manquer ? Demandez-moi où en sont vos suivis.",
  "Le bilan d'un client avance-t-il ? Posez-moi la question.",
  "Le suivi social d'un dossier vous intrigue ? Je regarde pour vous, il suffit de demander.",
  "Une formalité juridique en cours ? Demandez-moi où elle en est.",
  "Quels clients suivre en priorité aujourd'hui ? Demandez-le à EVA.",
  "Un client au téléphone et sa fiche introuvable ? Donnez-moi son nom.",
  "Je lis vos suivis plus vite qu'un tableur. Mettez-moi au défi !",
  "Une question bête ? Il n'y en a pas avec moi. Lancez-vous.",
  "Votre planning de la semaine ? Je vous le résume en un clic.",
  "Envie d'un récap rapide ? Cliquez sur moi et demandez.",
  "Je ne prends jamais de congés : posez-moi vos questions quand vous voulez.",
  "Un rendez-vous oublié ? Demandez-moi vos prochains rendez-vous.",
  "Le téléphone d'un client à retrouver ? Je l'ai peut-être déjà sous la main, demandez-moi.",
  "Où en est la CFE de ce client ? Posez-moi la question.",
  "Je connais vos dossiers… du moins ceux auxquels vous avez accès. Testez-moi !",
  "Fin de journée ? Demandez-moi ce qu'il vous reste à faire.",
  "Début de journée ? Demandez-moi vos priorités du jour.",
  "Chercher, c'est mon métier. Vous, demandez-moi simplement.",
  "Un collègue vous demande où en est un dossier ? Je peux vous aider à répondre.",
  "Je réponds en quelques secondes. Le chronomètre est lancé !",
  "Une info à vérifier avant un appel client ? Posez-moi la question.",
  "Les chiffres du jour vous intéressent ? Demandez-moi un point sur l'activité.",
  "Je ne mords pas, promis. Cliquez sur moi !",
  "Un dossier qui traîne ? Demandez-moi son état d'avancement.",
  "Gagnez du temps : posez-moi votre question plutôt que de chercher.",
  "Votre assistante est en ligne. Que puis-je faire pour vous ?",
  "Une question sur un client, un suivi ou un rendez-vous ? Je suis là.",
  "Et si vous me demandiez avant de chercher ? Je suis plutôt douée.",
];
// Phrases réservées à certains profils (voir profilEva) : les autres sont
// proposées à tout le monde.
const CONDITION_PHRASE = {
  "Un point sur l'activité du cabinet ? Je vous le résume en quelques lignes, il suffit de demander.": (p) => p.direction,
  "Les chiffres du jour vous intéressent ? Demandez-moi un point sur l'activité.": (p) => p.encadrement,
  "Le SIREN d'un client vous échappe ? Demandez-le-moi.": (p) => p.portefeuille,
  "Un client au téléphone et sa fiche introuvable ? Donnez-moi son nom.": (p) => p.portefeuille,
  "Le téléphone d'un client à retrouver ? Je l'ai peut-être déjà sous la main, demandez-moi.": (p) => p.portefeuille,
  "Où en est ce client ? Demandez à EVA, je m'occupe du reste.": (p) => p.portefeuille || p.suiviProd,
  "Une TVA à vérifier ? Posez-moi la question, je connais vos suivis sur le bout des circuits.": (p) => p.suiviProd,
  "Une échéance à ne pas manquer ? Demandez-moi où en sont vos suivis.": (p) => p.suiviProd,
  "Le bilan d'un client avance-t-il ? Posez-moi la question.": (p) => p.suiviProd,
  "Le suivi social d'un dossier vous intrigue ? Je regarde pour vous, il suffit de demander.": (p) => p.suiviProd,
  "Une formalité juridique en cours ? Demandez-moi où elle en est.": (p) => p.suiviProd,
  "Quels clients suivre en priorité aujourd'hui ? Demandez-le à EVA.": (p) => p.suiviProd,
  "Où en est la CFE de ce client ? Posez-moi la question.": (p) => p.suiviProd,
  "Un dossier qui traîne ? Demandez-moi son état d'avancement.": (p) => p.suiviProd,
};
const phrasesPour = (p) => PHRASES.filter((t) => !CONDITION_PHRASE[t] || CONDITION_PHRASE[t](p));

const PREMIERE_BULLE_MS = 1500; // le temps que la page s'affiche
// Délai entre deux apparitions (début à début) : environ 30 s.
const INTERVALLE_BULLE_MS = [27000, 33000];
const DUREE_BULLE_MS = 10000;
// Après la fermeture du chat : première bulle au bout de 30 s.
const BULLE_APRES_FERMETURE_MS = 30000;

// Prénom : les comptes MyALFA n'ont pas d'ordre stable entre name et
// lastName ; le nom de famille est celui écrit en majuscules.
const enMajuscules = (t) => !!t && t === t.toUpperCase() && t !== t.toLowerCase();
function prenomDe(u) {
  const a = (u?.name || "").trim();
  const b = (u?.lastName || "").trim();
  if (enMajuscules(a) && !enMajuscules(b)) return b;
  if (enMajuscules(b) && !enMajuscules(a)) return a;
  return b || a;
}

const lire = (cle) => {
  try {
    const v = JSON.parse(localStorage.getItem(cle));
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};


/* ---------- Icônes (mêmes tracés que le chat des Modules) ---------- */
const TRACES = {
  spark: (
    <>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 15l.7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7z" />
    </>
  ),
  send: (
    <>
      <path d="M4 12l16-8-6 16-2.5-6.5z" />
      <path d="M11.5 13.5L20 4" />
    </>
  ),
  // Calendrier + horloge : « Démarrer un rendez-vous » (maquette Claude Design).
  rdv: (
    <>
      <path d="M11 20H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3M3 9h16M8 2v4M14 2v4"></path>
      <circle cx="17" cy="17" r="5"></circle>
      <path d="M17 14.6V17l1.6 1"></path>
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  x: (
    <>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  check: <path d="M5 12l5 5L20 7" />,
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>
  ),
};
const Ico = ({ n, s = 16 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {TRACES[n]}
  </svg>
);

/* ---------- Mise en forme des réponses (comme ProcAiMsg) ---------- */
const enLigne = (s) =>
  s.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g).map((x, i) => {
    if (/^\*\*.+\*\*$/.test(x)) return <strong key={i}>{x.slice(2, -2)}</strong>;
    if (/^https?:\/\//.test(x))
      return (
        <a key={i} href={x} target="_blank" rel="noopener noreferrer">
          {x.length > 48 ? x.slice(0, 45) + "…" : x}
        </a>
      );
    return <React.Fragment key={i}>{x}</React.Fragment>;
  });

const EvaMsg = ({ text }) => {
  const blocs = [];
  (text || "").split("\n").forEach((l) => {
    const t = l.trim();
    const ul = t.match(/^[-•*]\s+(.*)/);
    const ol = t.match(/^(\d+)[.)]\s+(.*)/);
    const dernier = blocs[blocs.length - 1];
    if (ul) {
      if (dernier && dernier.k === "ul") dernier.items.push(ul[1]);
      else blocs.push({ k: "ul", items: [ul[1]] });
    } else if (ol) {
      if (dernier && dernier.k === "ol") dernier.items.push(ol[2]);
      else blocs.push({ k: "ol", items: [ol[2]], start: +ol[1] });
    } else if (t) blocs.push({ k: "p", t: t.replace(/^#+\s*/, "") });
  });
  return (
    <div className="eva-md">
      {blocs.map((b, i) =>
        b.k === "p" ? (
          <p key={i}>{enLigne(b.t)}</p>
        ) : b.k === "ul" ? (
          <ul key={i}>{b.items.map((x, j) => <li key={j}>{enLigne(x)}</li>)}</ul>
        ) : (
          <ol key={i} start={b.start}>{b.items.map((x, j) => <li key={j}>{enLigne(x)}</li>)}</ol>
        ),
      )}
    </div>
  );
};

/* ---------- Barre « Utilisation » ---------- */
const Utilisation = ({ quota }) => {
  if (!quota) return null;
  if (quota.sansQuota) {
    return (
      <div className="eva-usage">
        <div className="eva-usage-top">
          <span>Utilisation</span>
          <b>Sans limite</b>
        </div>
        <p className="eva-usage-note">Votre rôle vous permet de ne pas avoir de restriction de quota.</p>
      </div>
    );
  }
  const pct = Math.min(100, quota.pourcentage || 0);
  return (
    <div className="eva-usage" title="Quota du jour, renouvelé chaque nuit à minuit (heure de La Réunion)">
      <div className="eva-usage-top">
        <span>Utilisation</span>
        <b>{Math.round(pct)} %</b>
      </div>
      <div className="eva-usage-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
        <i className={pct >= 100 ? "plein" : pct >= 80 ? "alerte" : ""} style={{ width: `${Math.max(pct, pct > 0 ? 3 : 0)}%` }} />
      </div>
    </div>
  );
};

/* ---------- Carte d'action à confirmer ---------- */
const CarteAction = ({ msg, onDecider }) => {
  const a = msg.action;
  const etat = msg.etatAction || "en_attente";
  return (
    <div className="eva-act">
      <div className="eva-act-t">{a.titre}</div>
      {a.details && a.details.length > 0 && (
        <dl className="eva-act-d">
          {a.details.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {etat === "en_attente" || etat === "en_cours" ? (
        <div className="eva-act-b">
          <button className="eva-btn ghost" disabled={etat === "en_cours"} onClick={() => onDecider("annuler")}>
            Annuler
          </button>
          <button className="eva-btn primary" disabled={etat === "en_cours"} onClick={() => onDecider("confirmer")}>
            {etat === "en_cours" ? <span className="eva-spin" /> : <Ico n="check" s={14} />}
            {etat === "en_cours" ? "En cours…" : "Confirmer"}
          </button>
        </div>
      ) : (
        <div className={`eva-act-etat ${etat}`}>{etat === "confirme" ? "Confirmé" : etat === "annule" ? "Annulé" : "Non abouti"}</div>
      )}
    </div>
  );
};

/* ---------- Mascotte (comme ProcMascot, fixe sur tous les écrans) ---------- */
// delaiPremiereBulle : 1,5 s à l'arrivée sur la plateforme, environ 30 s
// après la fermeture du chat.
const EvaMascotte = ({ onOpen, phrases = PHRASES, delaiPremiereBulle = PREMIERE_BULLE_MS }) => {
  const [phrase, setPhrase] = React.useState(null);
  const [look, setLook] = React.useState({ x: 0, y: 0 });
  const ref = React.useRef(null);
  const pioche = React.useRef([]);
  // Dernière liste reçue (selon le profil), lue au moment de piocher sans
  // relancer le minuteur de la bulle.
  const listeRef = React.useRef(phrases);
  if (listeRef.current !== phrases) {
    listeRef.current = phrases;
    pioche.current = [];
  }

  // Bulle : première apparition dès l'arrivée, puis environ toutes les 30 s
  // (comptées à partir de cette première bulle), 10 s à chaque fois, avec une
  // phrase différente de la précédente.
  React.useEffect(() => {
    let suivant;
    let masquer;
    const montrer = () => {
      // Pioche mélangée : aucune phrase ne revient avant que toutes soient passées.
      const liste = listeRef.current;
      if (!pioche.current.length) {
        pioche.current = liste.map((_, k) => k).sort(() => Math.random() - 0.5);
      }
      setPhrase(liste[pioche.current.pop()]);
      masquer = setTimeout(() => setPhrase(null), DUREE_BULLE_MS);
      const [min, max] = INTERVALLE_BULLE_MS;
      suivant = setTimeout(montrer, min + Math.random() * (max - min));
    };
    suivant = setTimeout(montrer, delaiPremiereBulle);
    return () => {
      clearTimeout(suivant);
      clearTimeout(masquer);
    };
  }, [delaiPremiereBulle]);

  React.useEffect(() => {
    let raf;
    const move = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const d = Math.hypot(dx, dy) || 1;
        setLook({ x: (dx / d) * 2.5, y: (dy / d) * 2 });
      });
    };
    window.addEventListener("mousemove", move);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", move);
    };
  }, []);

  const ouvrir = () => {
    setPhrase(null);
    onOpen();
  };
  return (
    <div className={`eva-masc ${phrase ? "parle" : ""}`}>
      {phrase && (
        <div className="eva-masc-bubble" onClick={ouvrir} role="status">
          {phrase}
        </div>
      )}
      <button ref={ref} className="eva-masc-bot" onClick={ouvrir} title="Demander à EVA" aria-label="Ouvrir EVA, l'assistante IA">
        <span className="eva-masc-ant">
          <span></span>
        </span>
        <span className="eva-masc-head">
          <span className="eva-masc-face" style={{ transform: `translate(${look.x}px, ${look.y}px)` }}>
            <span className="eva-masc-eye"></span>
            <span className="eva-masc-eye"></span>
            <span className="eva-masc-mouth"></span>
          </span>
        </span>
      </button>
    </div>
  );
};

/* ---------- Panneau de discussion ---------- */
export default function EvaChat({ currentView }) {
  const { utilisateur } = useEtatGlobal();
  const ctxFiche = useEvaContexte();
  // Propositions et phrases de la bulle selon le rôle et les modules ouverts.
  const profil = React.useMemo(() => profilEva(utilisateur), [utilisateur]);
  const suggestions = suggestionsPour(profil, ctxFiche);
  const phrases = React.useMemo(() => phrasesPour(profil), [profil]);
  const cle = utilisateur?.id ? `myalfa_eva_chat_v1_${utilisateur.id}` : null;

  const [ouvert, setOuvert] = React.useState(false);
  const [rdvOuvert, setRdvOuvert] = React.useState(false);
  // Le chat a déjà été ouvert : à la fermeture, la bulle revient après ~30 s
  // (et non tout de suite).
  const [dejaOuvert, setDejaOuvert] = React.useState(false);
  const [msgs, setMsgs] = React.useState(() => (cle ? lire(cle) : []));
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [quota, setQuota] = React.useState(null);
  const scRef = React.useRef(null);
  const taRef = React.useRef(null);

  // Changement de compte (usurpation, reconnexion) : conversation de ce compte.
  // Changement de compte (« Se connecter en tant que », reconnexion) : on
  // reprend la conversation et le quota du compte désormais connecté — EVA
  // applique alors ses droits et ses restrictions (le serveur lit le jeton).
  React.useEffect(() => {
    setMsgs(cle ? lire(cle) : []);
    setQuota(null);
    setErr(null);
  }, [cle]);
  React.useEffect(() => {
    if (!cle) return;
    try {
      localStorage.setItem(cle, JSON.stringify(msgs.slice(-CONSERVES_MAX)));
    } catch (e) {
      // stockage indisponible : la conversation reste en mémoire
    }
  }, [msgs, cle]);
  React.useEffect(() => {
    if (scRef.current) scRef.current.scrollTop = scRef.current.scrollHeight;
  }, [msgs, busy, ouvert]);

  const chargerQuota = React.useCallback(async () => {
    try {
      const res = await authFetch(`${API}/ia/utilisation`);
      if (res.ok) setQuota(await res.json());
    } catch (e) {
      // barre masquée si la consommation est indisponible
    }
  }, []);

  React.useEffect(() => {
    if (!ouvert) return undefined;
    chargerQuota();
    if (taRef.current) taRef.current.focus();
    const echap = (e) => e.key === "Escape" && setOuvert(false);
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [ouvert, chargerQuota, cle]);

  if (!utilisateur || ECRANS_SANS_EVA.has(currentView)) return null;

  const libelleEcran = LIBELLES_ECRANS[currentView] || currentView || "";
  const quotaAtteint = !!quota?.atteint;

  const ask = async (q) => {
    q = (q || "").trim();
    if (!q || busy || quotaAtteint) return;
    const avant = msgs;
    setMsgs([...avant, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    setErr(null);
    try {
      const res = await authFetch(`${API}/eva/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: q,
          historique: avant.slice(-HISTORIQUE_MAX).map((m) => ({ role: m.role, content: m.content })),
          contexte: { ecran: currentView, ecranLibelle: libelleEcran, clientId: ctxFiche.clientId || null, leadId: ctxFiche.leadId || null },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.quota) setQuota(data.quota);
        const e = new Error(data.message || "Erreur " + res.status);
        e.lisible = !!data.message;
        throw e;
      }
      if (data.quota) setQuota(data.quota);
      setMsgs((m) => [
        ...m,
        {
          role: "assistant",
          content: (data.texte || "").trim() || "Je n'ai pas pu formuler de réponse.",
          ...(data.action ? { action: data.action, etatAction: "en_attente" } : {}),
        },
      ]);
    } catch (e) {
      console.error("EVA :", e);
      setErr(e.lisible ? e.message : "EVA n'a pas pu répondre. Réessayez dans un instant.");
      setMsgs(avant);
      setInput(q);
    } finally {
      setBusy(false);
    }
  };

  const decider = async (index, decision) => {
    const msg = msgs[index];
    if (!msg?.action) return;
    const majEtat = (etat) => setMsgs((m) => m.map((x, i) => (i === index ? { ...x, etatAction: etat } : x)));
    majEtat("en_cours");
    setErr(null);
    try {
      const res = await authFetch(`${API}/eva/chat/actions/${msg.action.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "L'action n'a pas pu aboutir.");
      majEtat(decision === "confirmer" ? "confirme" : "annule");
      setMsgs((m) => [...m, { role: "assistant", content: data.texte || "C'est noté.", pdfUrl: data.pdfUrl || null }]);
    } catch (e) {
      majEtat("erreur");
      setErr(e.message);
    }
  };

  // Rendez-vous assisté (HUD sombre), ouvert depuis l'en-tête d'EVA.
  const hudRdv = rdvOuvert && <RdvHud onClose={() => setRdvOuvert(false)} initialClient={ctxFiche.clientNom || ""} initialClientId={ctxFiche.clientId || null} />;

  if (!ouvert) {
    return (
      <div className="eva">
        {hudRdv}
        <EvaMascotte
          phrases={phrases}
          delaiPremiereBulle={dejaOuvert ? BULLE_APRES_FERMETURE_MS : PREMIERE_BULLE_MS}
          onOpen={() => {
            setDejaOuvert(true);
            setOuvert(true);
          }}
        />
      </div>
    );
  }

  return (
    <div className="eva">
      {hudRdv}
      <aside className="eva-panel" role="dialog" aria-label="EVA, l'assistante IA de MyALFA">
        <div className="eva-head">
          <span className="eva-logo">
            <Ico n="spark" s={16} />
          </span>
          <div className="eva-ht">
            <b>EVA</b>
            <small>L'assistante IA de MyALFA</small>
          </div>
          <button
            className="eva-hb"
            title="Démarrer un rendez-vous"
            onClick={() => {
              setOuvert(false);
              setRdvOuvert(true);
            }}
          >
            <Ico n="rdv" s={16} />
          </button>
          {msgs.length > 0 && (
            <button
              className="eva-hb"
              title="Nouvelle conversation"
              onClick={() => {
                setMsgs([]);
                setErr(null);
              }}
            >
              <Ico n="plus" s={15} />
            </button>
          )}
          <button className="eva-hb" title="Fermer" onClick={() => setOuvert(false)}>
            <Ico n="x" s={15} />
          </button>
        </div>
        <Utilisation quota={quota} />

        <div className="eva-scroll" ref={scRef}>
          {msgs.length === 0 && (
            <div className="eva-empty">
              <p>Bonjour {prenomDe(utilisateur)} ! Je suis EVA. Je réponds à partir des données de MyALFA auxquelles vous avez accès.</p>
              <div className="eva-sugg">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => ask(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="eva-u">
                {m.content}
              </div>
            ) : (
              <div key={i} className="eva-a">
                <EvaMsg text={m.content} />
                {m.action && <CarteAction msg={m} onDecider={(d) => decider(i, d)} />}
                {m.pdfUrl && (
                  <a className="eva-pdf" href={m.pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Ico n="file" s={14} /> Ouvrir le PDF
                  </a>
                )}
              </div>
            ),
          )}
          {busy && (
            <div className="eva-a eva-wait">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          {err && (
            <div className="eva-err">
              <Ico n="alert" s={14} /> {err}
            </div>
          )}
        </div>

        <div className="eva-foot">
          {(libelleEcran || ctxFiche.clientNom) && (
            <div className="eva-ctx">
              <Ico n="eye" s={12} /> Contexte : {[libelleEcran, ctxFiche.clientNom].filter(Boolean).join(" · ")}
            </div>
          )}
          {quotaAtteint ? (
            <div className="eva-err">
              <Ico n="alert" s={14} /> Vous avez utilisé 100 % de votre quota du jour. Il sera renouvelé demain à minuit.
            </div>
          ) : (
            <div className="eva-in">
              <textarea
                ref={taRef}
                rows={1}
                value={input}
                placeholder="Posez votre question à EVA…"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    ask(input);
                  }
                }}
              ></textarea>
              <button className="eva-send" disabled={!input.trim() || busy} onClick={() => ask(input)} title="Envoyer">
                <Ico n="send" s={15} />
              </button>
            </div>
          )}
          <div className="eva-note">EVA peut se tromper : vérifiez les informations importantes.</div>
        </div>
      </aside>
    </div>
  );
}
